import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Focus trap for modal/drawer components. Implements ARIA Authoring
 * Practices Guide dialog pattern:
 *   1. On mount: capture previously-focused element + focus first
 *      focusable inside the trap.
 *   2. While mounted: Tab cycles within the trap (Shift+Tab on first
 *      goes to last, Tab on last goes to first). Focusables are
 *      re-queried on each Tab so dynamic content works.
 *   3. On unmount: restore focus to the previously-focused element
 *      (if still attached to DOM — else noop).
 *
 * Compose with useEscapeKey for full keyboard UX: Esc closes,
 * Tab cycles. Returns the ref to attach to the modal's outermost
 * wrapper element.
 *
 * Empty modal (no focusables) is a no-op — no crash, no focus change.
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    function getFocusables(): HTMLElement[] {
      if (!container) return [];
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    }

    const focusables = getFocusables();
    if (focusables.length > 0) {
      // Don't steal focus if something inside the modal already has it
      // (e.g., an input with autoFocus attribute already ran).
      if (!container.contains(document.activeElement)) {
        focusables[0].focus();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;

      const current = getFocusables();
      if (current.length === 0) {
        e.preventDefault();
        return;
      }

      const first = current[0];
      const last = current[current.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the trigger element if it's still in the DOM.
      // isConnected guards against the trigger being unmounted while
      // the modal was open (e.g., the row that opened the modal got
      // re-rendered).
      if (previouslyFocused && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, []);

  return ref;
}
